import React, { useEffect } from 'react';

import { useSmartReload } from 'hooks/useSmartReload';
import { logger } from 'utils/logger';

// Expected computed background-color values from design tokens
const DARK_BG_RGB = 'rgb(10, 10, 10)';
const TRANSPARENT_BG = 'rgba(0, 0, 0, 0)';

interface SmartReloadWrapperProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that provides smart monitoring WITHOUT automatic reload
 * Must be used inside providers (MenuSelectionProvider, etc.)
 */
const SmartReloadWrapper: React.FC<SmartReloadWrapperProps> = ({ children }) => {
  // Hook for smart monitoring WITH menu detection
  const { isInMeeting, stats } = useSmartReload({
    minInterval: 120000, // 2 minutes
    meetingProtection: true,
    checkInterval: 5000, // 5 seconds
    enableMenuDetection: true, // Enable menu detection
  });

  useEffect(() => {
    // Theme monitoring WITHOUT automatic reload
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // When the page becomes visible again, only monitor the theme
        const bodyBackground = getComputedStyle(document.body).backgroundColor;
        const expectedBackground = DARK_BG_RGB;

        if (bodyBackground !== expectedBackground && bodyBackground !== TRANSPARENT_BG) {
          logger.warn(
            '[SmartReloadWrapper] Theme issue detected - user can reload manually if needed',
            {
              currentBackground: bodyBackground,
              expectedBackground,
              isInMeeting,
              currentMenu: stats.currentMenu,
              dynamicComponent: stats.dynamicComponent,
              timestamp: new Date().toISOString(),
            }
          );
          // WITHOUT automatic reload - logging only for debugging
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isInMeeting, stats]);

  // Log statistics for debugging (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[SmartReloadWrapper] Monitoramento Stats:', { data: stats });
    }
  }, [stats]);

  // This component renders nothing, only provides monitoring
  return <>{children}</>;
};

export default SmartReloadWrapper;
